import "@cdx/common/fonts/fonts.css";
import "@fontsource-variable/roboto-mono";
import "modern-normalize/modern-normalize.css";
import "../global-styles.css";
import "overlayscrollbars/overlayscrollbars.css";
import type {ReactNode} from "react";
import {useState, useEffect} from "react";
import {ClickOutsideProvider, KeyPressContextProvider, cx, errorToString} from "@cdx/common";
import UiHandlerContext from "@cdx/ds/components/DSForm/UiHandlerCtx";
import {useLocation, useHistory} from "react-router-dom";
import {rootStatusColorOverwrites} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import dsStyles from "@cdx/ds/css/index.css";
import {Box, Col, DSIconButton, DSIcon, Text} from "@cdx/ds";
import {elementThemes} from "@cdx/ds/css/themes/themes.css";
import {API_ERRORS, apiErrorEvent} from "../lib/request-utils";
import useSyncUserTimeSettings from "../lib/hooks/useSyncUserTimeSettings";
import {useRoot} from "../lib/mate/mate-utils";
import {BlingProvider} from "../lib/hooks/useBling";
import TimerWidget from "../features/time-tracking/TimerWidget";
import {SetupHints} from "../features/hints/HintManager";
import {ConfirmationRegistry} from "../lib/confirm";
import {SecondFactorHost} from "../features/auth/second-factor-gate";
import {exceptionEvent} from "../lib/error-logger";
import messenger, {MessengerArea} from "../lib/messenger";
import {MutationRefusal} from "../lib/mutation-refusal";
import {useBrowserCheck} from "../lib/use-browser-check";
import {isPublicContext} from "../lib/app-mode";
import ShortcutLister from "./ShortcutLister";
import PushUpdates from "./PushUpdates";
import Emoji from "./Markdown/Emoji";
import {DeploymentInfo} from "./DeploymentInfo";
import type {ModalRegistryProps} from "./Modal/ModalRegistry";
import ModalRegistry from "./Modal/ModalRegistry";
import {ApiErrors} from "./ErrorOverlays";
import {useTitlePostFix} from "./useTitle";

const uiHandlers = {
  onButtonError: (error: unknown) => {
    console.error("button click resulted in ", error);
    if (error instanceof MutationRefusal && error.shown) return;
    messenger.send(errorToString(error), {type: "error", msToRemove: 10000});
  },
};

const ErrorPill = ({onClose}: {onClose: () => void}) => (
  <Col
    position="fixed"
    zIndex={3}
    align="center"
    bottom="0"
    left="0"
    right="0"
    pb="12px"
    pointerEvents="none"
  >
    <Col
      rounded={4}
      pa="24px"
      colorTheme="elementThemeBase"
      className={elementThemes.alert}
      elevation={300}
      bg
      maxWidth="480px"
      width="100%"
      pointerEvents="auto"
      relative
    >
      <Box absolute style={{top: 15, right: 15}}>
        <DSIconButton
          icon={<DSIcon name="Close" label="Close" />}
          variant="tertiary"
          negatePadding
          onClick={onClose}
        />
      </Box>
      <Text type="prose16" color="primary">
        Something just broke <Emoji>😣</Emoji>
        <br />
        You might need to reload this page.
      </Text>
    </Col>
  </Col>
);

type RootProps = {
  children: ReactNode;
  notFound?: ReactNode;
  modals: ModalRegistryProps["modals"];
  isPublicProject?: boolean;
};

const Root = ({children, notFound = "404", modals, isPublicProject = false}: RootProps) => {
  const root = useRoot();
  const location = useLocation();
  const history = useHistory();
  const [got404, set404] = useState(false);
  const [showExceptionThrown, setShowExceptionThrown] = useState(false);
  useSyncUserTimeSettings(root);

  useEffect(() => {
    return exceptionEvent.addListener(() => setShowExceptionThrown(true));
  }, []);

  useEffect(() =>
    apiErrorEvent.addListener((e) => {
      if (e && e.type === API_ERRORS.API_ERROR && e.status === 404) {
        set404(true);
      }
    })
  );

  useBrowserCheck();

  const getTitlePostFix = () => {
    if (isPublicContext || !root.account) return null;
    return root.account.$meta.get("name", null);
  };
  useTitlePostFix(getTitlePostFix());

  const getPaletteClassNames = () => {
    const palette = root.loggedInUser?.$meta.get("statusColorPalette", "default");
    if (!palette || palette === "default") return null;
    return rootStatusColorOverwrites[palette];
  };
  const paletteClassNames = getPaletteClassNames();

  useEffect(() => {
    if (!paletteClassNames) return;
    const names = paletteClassNames.split(" ");
    names.forEach((c) => document.body.classList.add(c));
    return () => names.forEach((c) => document.body.classList.remove(c));
  }, [paletteClassNames]);

  if (got404 && (!root.account || !root.account.$meta.isLoaded)) {
    return notFound;
  }

  if (!root.account) {
    return (
      <Col pa="24px" sp="8px">
        <Text type="title24">Organization not found.</Text>
        <Text type="prose16">
          If this problem persists please get in touch with hello@codecks.io and tell us what
          happened to get here!
        </Text>
      </Col>
    );
  }

  return (
    <UiHandlerContext.Provider value={uiHandlers}>
      <KeyPressContextProvider>
        <ClickOutsideProvider>
          {(handlers) => (
            <div
              className={cx(
                dsStyles.position.relative,
                dsStyles.zIndex[0],
                dsStyles.minHeight["100%"]
              )}
              {...handlers}
            >
              {children}
              <ModalRegistry location={location} history={history} root={root} modals={modals} />
              <PushUpdates root={root} />
              <ShortcutLister />
              <MessengerArea />
              <ConfirmationRegistry />
              <SecondFactorHost />
              <DeploymentInfo />
              {!isPublicProject && <TimerWidget root={root} />}
              <BlingProvider />
              <ApiErrors history={history} location={location} />
              {showExceptionThrown && <ErrorPill onClose={() => setShowExceptionThrown(false)} />}
              <SetupHints root={root} />
            </div>
          )}
        </ClickOutsideProvider>
      </KeyPressContextProvider>
    </UiHandlerContext.Provider>
  );
};

export default Root;
